// PRC-050 (boceto S6, diseño apartado 7): revisión de un cambio de precio en
// la ficha del seguimiento. Cuatro indicadores antes / después con los mismos
// días a cada lado de la fecha efectiva (como máximo el plazo de revisión),
// unidades por semana con la fecha marcada, frase fija de no causalidad y
// «Registrar valoración» · «Añadir nota» · «Reprogramar revisión».
// La cronología sigue en el panel del expediente que va debajo.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, InputBase } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import MetricCard from '../../../pricingJourney/components/MetricCard';
import ProaDatePicker from '../../../../components/Common/ProaDatePicker';
import { J } from '../../../pricingJourney/journeyTokens';
import { formatNumber } from '../../../pricingJourney/formatNumber';
import {
  addMarginCenterFollowUpNote,
  closeMarginCenterFollowUp,
  getMarginCenterPriceChangeReview,
  updateMarginCenterFollowUpPlanning,
} from '../../services/marginCenterService';
import type { MarginCenterFollowUp } from '../../types/marginCenter';
import {
  REVIEW_SHORTCUTS,
  formatDayDate,
  reviewDateFor,
  reviewDateToIso,
  toDateOnly,
  type ReviewShortcut,
} from '../cadenceManagement/cadenceManagementTexts';
import {
  ASSESSMENT_OPTIONS,
  NO_CAUSALITY,
  assessmentConclusion,
  money,
  pctChange,
  rhythmLabel,
  rhythmNote,
  signedPct,
  type PriceChangeAssessment,
} from './priceChangeTexts';

type Mode = 'assess' | 'note' | 'reschedule';

const cardSx = { p: '18px 20px', borderRadius: J.radiusCard, border: '1px solid rgba(148,163,184,.16)', bgcolor: J.card, fontFamily: J.font, minWidth: 0 } as const;
const eyebrowSx = { fontSize: 11.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase', color: J.skyText } as const;
const lblSx = { fontSize: 12, fontWeight: 700, color: J.body, m: '14px 0 6px' } as const;
const inputSx = { width: '100%', border: '1px solid #334155', borderRadius: '10px', px: '12px', py: '7px', fontSize: 13.5, color: J.text, bgcolor: 'rgba(2,6,23,.5)', fontFamily: J.font } as const;
const btnSx = (primary: boolean) => ({
  height: 30, px: '12px', borderRadius: '10px', fontSize: 12.5, fontWeight: 700, fontFamily: J.font,
  ...(primary ? { bgcolor: J.sky, color: J.onSky } : { border: '1px solid #334155', color: J.text }),
}) as const;

const Chip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => (
  <ButtonBase onClick={onClick} aria-pressed={on} sx={{
    border: '1px solid', borderColor: on ? J.sky : '#334155', borderRadius: 999, px: '12px', py: '5px',
    fontSize: 12.5, fontWeight: 600, fontFamily: J.font, color: on ? J.skyText : J.body,
    bgcolor: on ? 'rgba(56,189,248,.14)' : 'transparent',
  }}>{children}</ButtonBase>
);

const tone = (value: number | null) => (value == null || Math.abs(value) < 0.05 ? 'neutral' : value > 0 ? 'up' : 'down') as 'neutral' | 'up' | 'down';

interface Props {
  followUp: MarginCenterFollowUp;
  /** «Resultado negativo»: abre una nueva evaluación de la relación; al cerrarla se vuelve a esta ficha. */
  onOpenNewEvaluation: (customerId: string, productId: string) => void;
}

const PriceChangeReviewPanel: React.FC<Props> = ({ followUp, onOpenNewEvaluation }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const review = useQuery({
    queryKey: ['margin-center', 'follow-ups', 'price-change-review', followUp.id],
    queryFn: () => getMarginCenterPriceChangeReview(followUp.id),
    retry: 1,
  });
  const data = review.data;
  const open = followUp.status !== 'closed';
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [mode, setMode] = useState<Mode>('assess');
  const [assessment, setAssessment] = useState<PriceChangeAssessment>('positive');
  const [conclusion, setConclusion] = useState('');
  const [edited, setEdited] = useState(false);
  const [note, setNote] = useState('');
  const [shortcut, setShortcut] = useState<ReviewShortcut | null>(null);
  const [otherDate, setOtherDate] = useState('');
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!edited) setConclusion(assessmentConclusion(assessment, data));
  }, [assessment, data, edited]);

  const reviewDate = shortcut === 'other' ? otherDate : shortcut ? reviewDateFor(shortcut) : null;
  const needsDate = mode === 'reschedule' || (mode === 'assess' && assessment === 'premature');
  const error = useMemo(() => {
    if (needsDate && !reviewDate) return 'Elige la nueva fecha de revisión.';
    if (mode === 'assess' && !conclusion.trim()) return 'Escribe la conclusión.';
    if (mode === 'note' && !note.trim()) return 'Escribe la nota.';
    return null;
  }, [needsDate, reviewDate, mode, conclusion, note]);

  const reset = () => { setAttempted(false); setNote(''); setShortcut(null); setOtherDate(''); setEdited(false); };
  const refresh = () => { void queryClient.invalidateQueries({ queryKey: ['margin-center', 'follow-ups'] }); };
  const label = `${data?.customer_label || followUp.customer_id || ''} · ${followUp.product_id ?? ''}`;

  const save = useMutation({
    mutationFn: async () => {
      if (mode === 'note') return addMarginCenterFollowUpNote(followUp.id, { content: note.trim() });
      const planning = (iso: string) => updateMarginCenterFollowUpPlanning(followUp.id, {
        expected_updated_at: followUp.updated_at, next_action: followUp.next_action, next_review_at: iso,
      });
      if (mode === 'reschedule') return planning(reviewDateToIso(reviewDate!));
      if (assessment === 'positive') {
        return closeMarginCenterFollowUp(followUp.id, { close_reason: 'explained', resolution_type: 'Resultado positivo', conclusion: conclusion.trim() });
      }
      if (assessment === 'premature') {
        await planning(reviewDateToIso(reviewDate!));
        return addMarginCenterFollowUpNote(followUp.id, { content: `Valoración: evaluación prematura. ${conclusion.trim()}` });
      }
      return addMarginCenterFollowUpNote(followUp.id, { content: `Valoración: resultado negativo. ${conclusion.trim()}` });
    },
    onSuccess: () => {
      const negative = mode === 'assess' && assessment === 'negative';
      const message = mode === 'note' ? 'Nota añadida.'
        : mode === 'reschedule' ? 'Revisión reprogramada.'
          : assessment === 'positive' ? `Seguimiento cerrado: ${label}`
            : assessment === 'premature' ? 'Valoración registrada; revisión reprogramada.'
              : 'Valoración registrada. Se abre una nueva evaluación de la relación.';
      enqueueSnackbar(message, { variant: 'success' });
      reset();
      setMode('assess');
      refresh();
      if (negative && followUp.customer_id && followUp.product_id) onOpenNewEvaluation(followUp.customer_id, followUp.product_id);
    },
    onError: (failure: unknown) => {
      const detail = (failure as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      enqueueSnackbar(typeof detail === 'string' && detail ? detail : 'No se pudo guardar. Actualiza la ficha y vuelve a intentarlo.', { variant: 'error' });
      refresh();
    },
  });

  // Se monta en toda ficha cliente-producto: mientras no se sepa que es un
  // seguimiento de precio no pinta nada (la ficha de siempre no cambia).
  if (review.isPending || review.isError) return null;
  if (!data || data.status === 'not_price_change' || data.status === 'not_found') return null;

  const before = data.before;
  const after = data.after;
  const currency = data.application?.currency_code;
  const priceChange = pctChange(before?.charged_price, after?.charged_price);
  const unitsChange = pctChange(before?.units, after?.units);
  const marginChange = pctChange(before?.margin, after?.margin);
  const weeks = data.weekly_units ?? [];
  const maxUnits = Math.max(1, ...weeks.map((w) => Math.max(0, Number(w.units) || 0)));
  const effective = data.application?.effective_date ?? null;
  const goTo = (next: Mode) => {
    setMode(next); setAttempted(false);
    actionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (error || save.isPending) return;
    save.mutate();
  };
  const primary = mode === 'note' ? 'Añadir nota'
    : mode === 'reschedule' ? 'Reprogramar'
      : assessment === 'positive' ? 'Cerrar seguimiento'
        : assessment === 'premature' ? 'Reprogramar' : 'Registrar y abrir evaluación';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', fontFamily: J.font, color: J.text }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={eyebrowSx}>Seguimiento · cambio de precio</Box>
          <Box component="h2" sx={{ m: '4px 0 0', fontSize: 20, fontWeight: 800 }}>
            {data.customer_label} · {data.product_id}{data.product_label && data.product_label !== data.product_id ? ` · ${data.product_label}` : ''}
          </Box>
          <Box sx={{ mt: '2px', fontSize: 12.5, color: J.muted }}>
            Precio aplicado {money(data.application?.applied_price, currency)} por {data.application?.unit_basis} desde el {formatDayDate(effective)}
            {' · '}{data.window_days ?? 0} días antes y después{data.data_cutoff_date ? ` · datos hasta el ${formatDayDate(data.data_cutoff_date)}` : ''}
          </Box>
        </Box>
        {open && (
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <ButtonBase onClick={() => goTo('note')} sx={btnSx(false)}>Añadir nota</ButtonBase>
            <ButtonBase onClick={() => goTo('reschedule')} sx={btnSx(false)}>Reprogramar revisión</ButtonBase>
            <ButtonBase onClick={() => goTo('assess')} sx={btnSx(true)}>Registrar valoración</ButtonBase>
          </Box>
        )}
      </Box>

      {data.status === 'too_early' ? (
        <Box sx={{ ...cardSx, fontSize: 13.5, color: J.body }}>
          Todavía no hay ventas posteriores a la fecha efectiva con las que comparar. La revisión se completa a medida que llegan los datos.
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(4, minmax(0,1fr))' }, gap: '12px' }}>
          <MetricCard label="Precio cobrado" value={money(after?.charged_price, currency)} changeTone={tone(priceChange)}
            change={`antes ${money(before?.charged_price, currency)} · ${signedPct(priceChange)}`} />
          <MetricCard label="Unidades" value={formatNumber(after?.units ?? null, { decimals: 0 })} changeTone={tone(unitsChange)}
            change={`antes ${formatNumber(before?.units ?? null, { decimals: 0 })} · ${signedPct(unitsChange)}`} />
          <MetricCard label="Margen" value={after?.margin == null ? 'No calculable' : money(after.margin, currency, 0)}
            valueColor={after?.margin == null ? J.muted : undefined} changeTone={tone(marginChange)}
            change={before?.margin == null ? 'antes no calculable' : `antes ${money(before.margin, currency, 0)} · ${signedPct(marginChange)}`}
            note={after?.margin == null || before?.margin == null ? 'Falta coste en alguna venta' : undefined} />
          <MetricCard label="Ritmo de compra" value={rhythmLabel(data.rhythm)}
            valueColor={data.rhythm?.out_of_pattern ? J.amber : undefined} note={rhythmNote(data.rhythm, after)} />
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: open ? 'minmax(0,1.35fr) minmax(0,1fr)' : '1fr' }, gap: '14px', alignItems: 'start' }}>
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <Box sx={eyebrowSx}>Evolución semanal de unidades</Box>
            <Box sx={{ fontSize: 12, color: J.muted }}>
              {data.window_days ?? 0} días antes · cambio el {formatDayDate(effective)} · {data.window_days ?? 0} días después
            </Box>
          </Box>
          {weeks.length === 0 ? (
            <Box sx={{ mt: '12px', fontSize: 13, color: J.muted }}>Sin semanas que comparar todavía.</Box>
          ) : (
            <Box sx={{ mt: '12px', height: 220, display: 'flex', alignItems: 'flex-end', gap: '4px' }} role="img"
              aria-label={`Unidades por semana antes y después del ${formatDayDate(effective)}`}>
              {weeks.map((w) => (
                <React.Fragment key={w.week_index}>
                  {w.week_index === 0 && <Box sx={{ width: '2px', alignSelf: 'stretch', bgcolor: J.amber, mx: '3px', flex: '0 0 auto' }} title={`Cambio de precio · ${formatDayDate(effective)}`} />}
                  <Box title={`${formatDayDate(w.from)} – ${formatDayDate(w.to)} · ${formatNumber(w.units, { decimals: 0 })} uds.`}
                    sx={{ flex: 1, minWidth: 4, height: `${Math.max(2, (Math.max(0, Number(w.units) || 0) / maxUnits) * 100)}%`, borderRadius: '4px 4px 0 0',
                      bgcolor: w.after ? J.sky : 'rgba(148,163,184,.45)' }} />
                </React.Fragment>
              ))}
            </Box>
          )}
          <Box sx={{ mt: '10px', fontSize: 12.5, color: J.muted }}>{NO_CAUSALITY}</Box>
        </Box>

        {open && (
          <Box ref={actionsRef} component="form" onSubmit={submit} sx={cardSx}>
            <Box sx={eyebrowSx}>{mode === 'note' ? 'Añadir nota' : mode === 'reschedule' ? 'Reprogramar revisión' : 'Registrar valoración'}</Box>
            {mode === 'assess' && (
              <Box sx={{ display: 'grid', gap: '6px', mt: '10px' }}>
                {ASSESSMENT_OPTIONS.map((o) => {
                  const on = assessment === o.id;
                  return (
                    <ButtonBase key={o.id} onClick={() => { setAssessment(o.id); setEdited(false); setAttempted(false); }} aria-pressed={on}
                      sx={{ display: 'block', textAlign: 'left', p: '9px 12px', borderRadius: '10px', border: '1px solid', fontFamily: J.font,
                        borderColor: on ? J.sky : '#334155', bgcolor: on ? 'rgba(56,189,248,.10)' : 'transparent' }}>
                      <Box sx={{ fontSize: 13.5, fontWeight: 800, color: J.text }}>{o.label}</Box>
                      <Box sx={{ fontSize: 12, color: J.muted }}>{o.hint}</Box>
                    </ButtonBase>
                  );
                })}
              </Box>
            )}
            {needsDate && (
              <>
                <Box sx={lblSx}>Nueva fecha de revisión</Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {REVIEW_SHORTCUTS.map((s) => {
                    const on = shortcut === s.id;
                    const date = s.id !== 'other' ? reviewDateFor(s.id) : null;
                    return <Chip key={s.id} on={on} onClick={() => setShortcut(s.id)}>{on && date ? `${s.label} · ${formatDayDate(date)}` : s.label}</Chip>;
                  })}
                </Box>
                {shortcut === 'other' && (
                  <Box sx={{ mt: '10px', maxWidth: 260 }}>
                    <ProaDatePicker label="Fecha de revisión" value={otherDate} onChange={setOtherDate} size="small" minDate={toDateOnly(new Date())} />
                  </Box>
                )}
              </>
            )}
            {mode === 'assess' && (
              <>
                <Box sx={lblSx}>Conclusión <Box component="em" sx={{ fontStyle: 'normal', fontWeight: 500, color: '#64748b' }}>(generada; editable)</Box></Box>
                <InputBase multiline minRows={2} value={conclusion} onChange={(e) => { setConclusion(e.target.value); setEdited(true); }}
                  inputProps={{ 'aria-label': 'Conclusión', maxLength: 3800 }} sx={inputSx} />
              </>
            )}
            {mode === 'note' && (
              <>
                <Box sx={lblSx}>Nota</Box>
                <InputBase autoFocus multiline minRows={3} value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ 'aria-label': 'Nota', maxLength: 4000 }} sx={inputSx} />
              </>
            )}
            {attempted && error && <Box sx={{ fontSize: 12, color: J.dangerText, mt: '8px' }}>{error}</Box>}
            <Box sx={{ pt: '14px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              {mode !== 'assess' && <ButtonBase onClick={() => { reset(); setMode('assess'); }} sx={btnSx(false)}>Cancelar</ButtonBase>}
              <ButtonBase type="submit" disabled={save.isPending} sx={btnSx(true)}>{save.isPending ? 'Guardando…' : primary}</ButtonBase>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PriceChangeReviewPanel;
